import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { FieldGroupWrapper } from '../../style/StyledElement'
import SpinnerButton from '../../components/common/SpinnerButton'
import AgGridWrapper from '../../components/common/AgGridWrapper'
import { apiConfig } from '../../api/ApiConfig'
import { hmdbHeaders } from '../../configs/GridConfig'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Divider from '@mui/material/Divider'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { styled } from '@mui/material/styles'
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  FormControl,
  FormLabel,
  Alert,
  Collapse,
  IconButton,
  Tooltip,
  InputAdornment,
  Chip
} from '@mui/material'
import { 
  Clear as ClearIcon, 
  Search as SearchIcon, 
  Info as InfoIcon,
  History as HistoryIcon 
} from '@mui/icons-material'

const { getHmdbData, getHmdbDataBySmile } = apiConfig()

// Constants
const SEARCH_STATES = {
  INIT: 'INIT',
  LOADING: 'LOADING',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR',
};

const SEARCH_OPTIONS = {
  SMILES: 'smiles',
  UNIPROT: 'uniprot'
}

// Enhanced validation patterns
const VALIDATION_PATTERNS = {
  SMILES: {
    basic: /^[A-Za-z0-9@+\-\[\]()=#$:.%\\\/\s]+$/,
    minLength: 3,
    maxLength: 1000
  },
  UNIPROT: {
    standard: /^[OPQ][0-9][A-Z0-9]{3}[0-9]|[A-NR-Z][0-9]([A-Z][A-Z0-9]{2}[0-9]){1,2}$/i,
    relaxed: /^[A-Z0-9_]{6,20}$/i,
    minLength: 6,
    maxLength: 20
  }
};

// Styled components for professional appearance
const HeaderContainer = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
  color: '#FFFFFF',
  padding: theme.spacing(3, 4),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
}))

const ContentContainer = styled(Paper)(({ theme }) => ({
  background: '#FFFFFF',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  border: '1px solid #E5E7EB',
  marginBottom: theme.spacing(2),
}))

const SearchSection = styled(Card)(({ theme }) => ({
  background: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}))

const ResultsSection = styled(Box)(({ theme }) => ({
  background: '#FFFFFF',
  border: '1px solid #E5E7EB',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}))

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiFilledInput-root': {
    backgroundColor: '#FFFFFF',
    border: '1px solid #D1D5DB',
    borderRadius: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#FFFFFF',
      borderColor: '#6B7280',
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      borderColor: '#4B5563',
    }
  },
  '& .MuiInputLabel-root': {
    color: '#6B7280',
    '&.Mui-focused': {
      color: '#4B5563',
    }
  }
}))

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  '& .MuiFormControlLabel-root': {
    '& .MuiRadio-root': {
      color: '#6B7280',
      '&.Mui-checked': {
        color: '#4B5563',
      }
    }
  }
}))

const FeatureChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: '#FFFFFF',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  }
}))

const HistoryChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#F3F4F6',
  border: '1px solid #D1D5DB',
  color: '#374151',
  '&:hover': {
    backgroundColor: '#E5E7EB',
    borderColor: '#6B7280',
  }
}))

export const HmdbSearch = ({ 
  uniprotInput, 
  data,
  onSearchComplete,
  onError
}) => {
  const [searchState, setSearchState] = useState(SEARCH_STATES.INIT)
  const [smiles, setSmiles] = useState('')
  const [uniprot, setUniprot] = useState('')
  const [searchResult, setSearchResult] = useState([])
  const [selectedOption, setSelectedOption] = useState(SEARCH_OPTIONS.SMILES)
  const [error, setError] = useState(null)
  const [showAdvancedInfo, setShowAdvancedInfo] = useState(false)
  const [searchHistory, setSearchHistory] = useState([])
  const [validationWarning, setValidationWarning] = useState(null)
  
  const inputRef = useRef(null)
  const searchButtonRef = useRef(null)

  // Initialize search history from localStorage
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem('hmdb_search_history')
      if (savedHistory) {
        const parsed = JSON.parse(savedHistory)
        setSearchHistory(Array.isArray(parsed) ? parsed.slice(-10) : []) // Keep last 10 searches
      }
    } catch (error) {
      console.warn('Failed to load search history:', error)
    }
  }, [])

  // Save search history to localStorage
  const saveToHistory = useCallback((term, type, resultCount) => {
    const newEntry = {
      term: term.trim(),
      type,
      timestamp: Date.now(),
      resultCount
    }
    
    const updatedHistory = [newEntry, ...searchHistory.filter(
      item => !(item.term === newEntry.term && item.type === newEntry.type)
    )].slice(0, 10)
    
    setSearchHistory(updatedHistory)
    
    try {
      localStorage.setItem('hmdb_search_history', JSON.stringify(updatedHistory))
    } catch (error) {
      console.warn('Failed to save search history:', error)
    }
  }, [searchHistory])

  // Handle radio button changes
  const handleRadioChange = useCallback((event) => {
    const value = event.target.value
    setSelectedOption(value)
    setSmiles('')
    setUniprot('')
    setError(null)
    setValidationWarning(null)
    setSearchState(SEARCH_STATES.INIT)
    
    // Focus input after option change
    setTimeout(() => inputRef.current?.focus(), 100)
  }, [])

  // Initialize state from props
  useEffect(() => {
    if (uniprotInput) {
      setUniprot(uniprotInput)
      setSelectedOption(SEARCH_OPTIONS.UNIPROT)
    }
    if (data && data.length > 0) {
      setSearchResult(data)
      setSearchState(SEARCH_STATES.COMPLETE)
    }
  }, [uniprotInput, data])

  // Get current input value based on selected option
  const currentInputValue = useMemo(() => {
    return selectedOption === SEARCH_OPTIONS.SMILES ? smiles : uniprot
  }, [selectedOption, smiles, uniprot])

  // Enhanced validation with detailed feedback
  const validateInput = useCallback((inputValue) => {
    const trimmedInput = inputValue.trim()
    
    if (!trimmedInput) {
      return {
        isValid: false,
        error: `${selectedOption === SEARCH_OPTIONS.SMILES ? 'SMILES' : 'Uniprot ID'} is required`
      }
    }

    if (selectedOption === SEARCH_OPTIONS.SMILES) {
      const { basic, minLength, maxLength } = VALIDATION_PATTERNS.SMILES
      
      if (trimmedInput.length < minLength) {
        return {
          isValid: false,
          error: `SMILES must be at least ${minLength} characters long`
        }
      }
      
      if (trimmedInput.length > maxLength) {
        return {
          isValid: false,
          error: `SMILES cannot exceed ${maxLength} characters`
        }
      }
      
      if (!basic.test(trimmedInput)) {
        return {
          isValid: false,
          error: 'SMILES contains invalid characters'
        }
      }
    } else {
      const { standard, relaxed, minLength, maxLength } = VALIDATION_PATTERNS.UNIPROT
      
      if (trimmedInput.length < minLength || trimmedInput.length > maxLength) {
        return {
          isValid: false,
          error: `Uniprot ID must be between ${minLength} and ${maxLength} characters`
        }
      }
      
      if (!standard.test(trimmedInput)) {
        if (!relaxed.test(trimmedInput)) {
          return {
            isValid: false,
            error: 'Invalid Uniprot ID format. Expected format: P12345, Q9Y6R4, etc.'
          }
        } else {
          return {
            isValid: true,
            warning: 'This appears to be a non-standard Uniprot ID format'
          }
        }
      }
    }

    return { isValid: true }
  }, [selectedOption])

  // Handle input changes with real-time validation
  const handleInputChange = useCallback((event) => {
    const value = event.target.value
    setError(null)
    setValidationWarning(null)
    
    if (selectedOption === SEARCH_OPTIONS.SMILES) {
      setSmiles(value)
    } else {
      setUniprot(value)
    }

    // Real-time validation for user feedback
    if (value.trim()) {
      const validation = validateInput(value)
      if (validation.warning) {
        setValidationWarning(validation.warning)
      }
    }
  }, [selectedOption, validateInput])

  // Handle history item selection
  const handleHistorySelect = useCallback((historyItem) => {
    setSelectedOption(historyItem.type)
    if (historyItem.type === SEARCH_OPTIONS.SMILES) {
      setSmiles(historyItem.term)
      setUniprot('')
    } else {
      setUniprot(historyItem.term)
      setSmiles('')
    }
    setError(null)
    setValidationWarning(null)
  }, [])

  // Clear input
  const handleClearInput = useCallback(() => {
    if (selectedOption === SEARCH_OPTIONS.SMILES) {
      setSmiles('')
    } else {
      setUniprot('')
    }
    setError(null)
    setValidationWarning(null)
    setSearchState(SEARCH_STATES.INIT)
    inputRef.current?.focus()
  }, [selectedOption])

  // Main search function
  const search = useCallback(async () => {
    const inputValue = currentInputValue.trim()
    const validation = validateInput(inputValue)
    
    if (!validation.isValid) {
      setError(validation.error || 'Invalid input')
      setSearchState(SEARCH_STATES.ERROR)
      onError?.(validation.error || 'Invalid input')
      return false
    }

    setSearchState(SEARCH_STATES.LOADING)
    setError(null)
    setValidationWarning(null)

    try {
      let apiResponse = null

      if (selectedOption === SEARCH_OPTIONS.UNIPROT) {
        apiResponse = await getHmdbData(inputValue)
      } else if (selectedOption === SEARCH_OPTIONS.SMILES) {
        apiResponse = await getHmdbDataBySmile(inputValue)
      }

      if (!apiResponse) {
        throw new Error('No response received from server')
      }

      const resultArray = Array.isArray(apiResponse) ? apiResponse : []
      setSearchResult(resultArray)
      setSearchState(SEARCH_STATES.COMPLETE)
      
      // Save to history and call callbacks
      saveToHistory(inputValue, selectedOption, resultArray.length)
      onSearchComplete?.(resultArray, inputValue, selectedOption)
      
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred'
      setError(errorMessage)
      setSearchState(SEARCH_STATES.ERROR)
      console.error('HMDB search error:', err)
      onError?.(errorMessage)
      return false
    }
  }, [currentInputValue, validateInput, selectedOption, saveToHistory, onSearchComplete, onError])

  // Handle Enter key press
  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter' && !isSearchDisabled) {
      event.preventDefault()
      search()
    }
  }, [search])

  // Memoized derived states
  const isLoading = useMemo(() => searchState === SEARCH_STATES.LOADING, [searchState])
  const hasError = useMemo(() => searchState === SEARCH_STATES.ERROR, [searchState])
  const isSearchDisabled = useMemo(() => !currentInputValue.trim() || isLoading, [currentInputValue, isLoading])
  const hasResults = useMemo(() => searchResult.length > 0, [searchResult])

  // Get input configuration
  const inputConfig = useMemo(() => {
    const isSmiles = selectedOption === SEARCH_OPTIONS.SMILES
    return {
      label: isSmiles ? 'SMILES Notation' : 'UniProt ID',
      placeholder: isSmiles ? 'Enter SMILES notation (e.g., CCO, c1ccccc1)' : 'Enter UniProt ID (e.g., P12345)',
      helperText: isSmiles 
        ? 'Enter a valid SMILES string to search HMDB metabolite data'
        : 'Enter a UniProt protein identifier to find associated metabolites'
    }
  }, [selectedOption])

  // Recent searches for the current search type
  const recentSearches = useMemo(() => 
    searchHistory
      .filter(item => item.type === selectedOption)
      .slice(0, 5), 
    [searchHistory, selectedOption]
  )

  return (
    <Box>
      {/* Professional Header */}
      <HeaderContainer elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 600, 
                marginBottom: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              HMDB Metabolite Search
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.9,
                fontSize: '1.1rem',
                lineHeight: 1.5,
                marginBottom: 2
              }}
            >
              Search the Human Metabolome Database for comprehensive metabolite information
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <FeatureChip label="Metabolomics" size="small" />
              <FeatureChip label="HMDB Database" size="small" />
              <FeatureChip label="Biomarker Discovery" size="small" />
            </Box>
          </Box>
          <Box sx={{ 
            display: { xs: 'none', md: 'block' },
            opacity: 0.3,
            fontSize: '4rem',
            fontWeight: 300
          }}>
            🧪
          </Box>
        </Box>
      </HeaderContainer>

      {/* Main Content */}
      <ContentContainer elevation={0}>
        {/* Search Configuration Section */}
        <SearchSection elevation={0}>
          <CardContent>
            <Typography 
              variant="h6" 
              sx={{ 
                marginBottom: 2, 
                color: '#374151',
                fontWeight: 600
              }}
            >
              Search Configuration
            </Typography>
            <Divider sx={{ marginBottom: 3, backgroundColor: '#E5E7EB' }} />

            <FieldGroupWrapper>
              <FormControl component="fieldset" fullWidth>
                <FormLabel 
                  component="legend" 
                  sx={{ 
                    mb: 1,
                    color: '#4B5563',
                    fontWeight: 500
                  }}
                >
                  Search Type
                  <Tooltip title="Toggle advanced information about search types">
                    <IconButton
                      size="small"
                      onClick={() => setShowAdvancedInfo(!showAdvancedInfo)}
                      sx={{ ml: 1 }}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </FormLabel>
                
                <StyledRadioGroup 
                  value={selectedOption} 
                  onChange={handleRadioChange}
                  row
                  sx={{ mb: showAdvancedInfo ? 1 : 2 }}
                >
                  <FormControlLabel 
                    value={SEARCH_OPTIONS.SMILES} 
                    control={<Radio />} 
                    label="SMILES Structure" 
                  />
                  <FormControlLabel
                    value={SEARCH_OPTIONS.UNIPROT}
                    control={<Radio />}
                    label="UniProt ID"
                  />
                </StyledRadioGroup>

                <Collapse in={showAdvancedInfo}>
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mb: 2,
                      backgroundColor: '#EFF6FF',
                      borderColor: '#BFDBFE',
                      color: '#1E40AF'
                    }}
                  >
                    <strong>SMILES:</strong> Search metabolites by chemical structure notation<br />
                    <strong>UniProt ID:</strong> Search metabolites associated with specific proteins
                  </Alert>
                </Collapse>
              </FormControl>
            </FieldGroupWrapper>

            <FieldGroupWrapper>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <StyledTextField
                    inputRef={inputRef}
                    variant="filled"
                    label={inputConfig.label}
                    placeholder={inputConfig.placeholder}
                    value={currentInputValue}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    error={Boolean(error)}
                    helperText={error || validationWarning || inputConfig.helperText}
                    fullWidth
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: currentInputValue && (
                        <InputAdornment position="end">
                          <Tooltip title="Clear input">
                            <IconButton
                              size="small"
                              onClick={handleClearInput}
                              disabled={isLoading}
                            >
                              <ClearIcon />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                    FormHelperTextProps={{
                      sx: { color: validationWarning ? 'warning.main' : undefined }
                    }}
                  />
                  
                  {/* Recent searches */}
                  {recentSearches.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <HistoryIcon fontSize="small" sx={{ color: '#6B7280' }} />
                        <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 500 }}>
                          Recent Searches:
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {recentSearches.map((item, index) => (
                          <HistoryChip
                            key={index}
                            label={`${item.term} (${item.resultCount})`}
                            size="small"
                            onClick={() => handleHistorySelect(item)}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
                
                <Box sx={{ mt: 1 }}>
                  <SpinnerButton
                    ref={searchButtonRef}
                    label="Search HMDB"
                    className="btn-primary"
                    onClick={search}
                    loading={isLoading}
                    disabled={isSearchDisabled}
                    sx={{
                      background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4B5563 0%, #374151 100%)',
                      },
                      fontWeight: 600,
                      textTransform: 'none',
                      minWidth: '120px'
                    }}
                  />
                </Box>
              </Box>
            </FieldGroupWrapper>
          </CardContent>
        </SearchSection>

        {/* Results Section */}
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              marginBottom: 2, 
              color: '#374151',
              fontWeight: 600
            }}
          >
            Metabolite Results
          </Typography>
          <Divider sx={{ marginBottom: 2, backgroundColor: '#E5E7EB' }} />

          {/* Results summary */}
          {searchState === SEARCH_STATES.COMPLETE && (
            <Alert 
              severity={hasResults ? 'success' : 'info'} 
              sx={{ 
                mb: 2,
                backgroundColor: hasResults ? '#ECFDF5' : '#EFF6FF',
                borderColor: hasResults ? '#A7F3D0' : '#BFDBFE',
                color: hasResults ? '#065F46' : '#1E40AF'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography>
                  {hasResults 
                    ? <>Found <strong>{searchResult.length}</strong> metabolite{searchResult.length !== 1 ? 's' : ''} for <strong>"{currentInputValue}"</strong></>
                    : <>No metabolites found for <strong>"{currentInputValue}"</strong>. Try a different search term.</>
                  }
                </Typography>
              </Box>
            </Alert>
          )}

          <ResultsSection>
            <AgGridWrapper
              data={searchResult}
              headers={hmdbHeaders}
              loading={isLoading}
              error={hasError}
              api={search}
            />
          </ResultsSection>
        </Box>
      </ContentContainer>
    </Box>
  )
}
